/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ActionIcon,
  Anchor,
  Avatar,
  Center,
  Group,
  rem,
  ScrollArea,
  Table,
  Text,
  Tooltip,
  UnstyledButton
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronUp,
  IconPencil,
  IconSelector,
  IconTrash
} from "@tabler/icons-react";
import cx from "clsx";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ThProps } from "../../InterfaceProps";
import ConfirmDeletePatientModal from "../ConfirmDeletePatientModal";
import EditPatientModal from "../EditPatientModal";
import classes from "./PatientTable.module.css";

interface RowData {
  name: string;
  client: string;
  updatedAt: string;
  createdAt: string;
}

const PatientTable = ({
  data,
  loadMorePatients,
  isLoading,
  fetchPatients,
  search
}: any) => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [patient, setPatient] = useState<any>(null);
  const [modalOpened, setModalOpened] = useState(false);
  const [modalConfirmDeleteOpened, setModalConfirmDeleteOpened] =
    useState(false);
  const viewport = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<any>(null);
  const [scrollPosition, onScrollPositionChange] = useState({ x: 0, y: 0 });
  useEffect(() => {
    setScrolled(scrollPosition.y != 0);
  }, [scrollPosition]);

  const [sortedData, setSortedData] = useState<any>([]);
  useEffect(() => {
    setSortedData(data);
  }, [data]);

  useEffect(() => {
    setSortedData(
      sortData(data, { sortBy, reversed: !reverseSortDirection, search })
    );
  }, [search]);

  const filterData = (data: RowData[], search: string) => {
    const query = search?.toLowerCase()?.trim();
    const keysFilter: any = ["client", "name"];
    return data?.filter((item: any) =>
      keysFilter.some((key: any) => item[key]?.toLowerCase()?.includes(query))
    );
  };

  const rows = sortedData.map((row: any) => {
    const strCreated = moment(row.createdAt).format("MM/DD/YYYY, hh:mm A");
    const strUpdated = moment(row.updatedAt).format("MM/DD/YYYY, hh:mm A");
    const isUseImage = localStorage.getItem("useImages") == "true";
    return (
      <Table.Tr key={row.id} style={{ cursor: "auto" }}>
        <Table.Td>
          <Anchor onClick={() => navigate(`/cases/${row.id}`, { state: row })}>
            <Group gap={5}>
              {isUseImage && (
                <Avatar src={row.s3Image} size={"sm"} mr={0} pr={0} />
              )}
              {row.name}
            </Group>
          </Anchor>
        </Table.Td>
        <Table.Td>{row.client}</Table.Td>
        <Table.Td>{strCreated}</Table.Td>
        <Table.Td>{strUpdated}</Table.Td>
        <Table.Td className="text-center">
          <Tooltip label={"Edit patient"} withArrow>
            <ActionIcon
              size="sm"
              mx={3}
              variant="subtle"
              color="gray"
              radius={20}
              onClick={() => {
                setPatient(row);
                setModalOpened(true);
              }}
            >
              <IconPencil size={20} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={"Delete patient"} withArrow>
            <ActionIcon
              mx={3}
              color="red"
              size="sm"
              variant="subtle"
              radius={20}
              onClick={() => {
                setPatient(row);
                setModalConfirmDeleteOpened(true);
              }}
            >
              <IconTrash size={20} />
            </ActionIcon>
          </Tooltip>
        </Table.Td>
      </Table.Tr>
    );
  });
  const [columns, setColumns] = useState<any>([
    { id: 1, name: "Patient Name", sortBy: "name" },
    { id: 2, name: "Client", sortBy: "client" },
    { id: 3, name: "Created", sortBy: "createdAt" },
    { id: 4, name: "Updated", sortBy: "updatedAt" },
    { id: 5, name: "Actions" }
  ]);
  const [resizing, setResizing] = useState({
    isResizing: false,
    columnIndex: -1,
    startX: 0,
    startWidth: 0
  });

  const handleMouseDown = (e: any, index: number) => {
    const colEl = document.getElementById(`col-${columns[index].id}`);
    setResizing({
      isResizing: true,
      columnIndex: index,
      startX: e.clientX,
      startWidth: colEl?.clientWidth as number
    });
  };

  const handleMouseMove = (e: any) => {
    if (!resizing.isResizing) return;

    let newWidth = resizing.startWidth + (e.clientX - resizing.startX);

    if (newWidth < 50) {
      newWidth = 50;
    }
    setColumns((prevColumns: any) =>
      prevColumns.map((col: any, index: number) =>
        index === resizing.columnIndex ? { ...col, width: newWidth } : col
      )
    );
  };

  const handleMouseUp = () => {
    try {
      if (resizing.isResizing) {
        setResizing({ ...resizing, isResizing: false });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const Th = ({
    children,
    reversed,
    sorted,
    onSort,
    index,
    style,
    id
  }: ThProps) => {
    const Icon = sorted
      ? reversed
        ? IconChevronUp
        : IconChevronDown
      : IconSelector;

    return (
      <Table.Th className={classes.th} id={id} style={style}>
        <UnstyledButton onClick={onSort} className={cx(classes.control)}>
          <Group justify="space-between">
            <Text fw={500} fz="sm">
              {children}
            </Text>
            <Center className={classes.icon}>
              <Icon style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
            </Center>
          </Group>
        </UnstyledButton>
        <div
          className="resize-handle"
          onMouseDown={(e) => handleMouseDown(e, index)}
        ></div>
      </Table.Th>
    );
  };
  const handleScroll = (event: any) => {
    const { scrollHeight, scrollTop, clientHeight } = event.target;
    if (scrollHeight - scrollTop === clientHeight && !isLoading) {
      loadMorePatients();
    }
  };
  const setSorting = (field: any) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    setSortedData(sortData(data, { sortBy: field, reversed, search }));
  };
  function sortData(
    data: RowData[],
    payload: { sortBy: keyof RowData | null; reversed: boolean; search: string }
  ) {
    const { sortBy } = payload;
    if (!sortBy) {
      return filterData(data, payload.search);
    }
    return filterData(
      [...data].sort((a, b) => {
        if (payload.reversed) {
          return b[sortBy]?.localeCompare(a[sortBy]);
        }

        return a[sortBy]?.localeCompare(b[sortBy]);
      }),
      payload.search
    );
  }

  const [sortBy, setSortBy] = useState<keyof RowData | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  return (
    <>
      <ScrollArea
        h={700}
        w={"100%"}
        scrollbars="xy"
        ref={scrollAreaRef}
        onTouchMove={handleScroll}
        onWheel={handleScroll}
        onScrollPositionChange={onScrollPositionChange}
        viewportRef={viewport}
      >
        <div onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}>
          <Table className="vetscribe-table">
            <Table.Thead className={cx("header", { ["scrolled"]: scrolled })}>
              <Table.Tr>
                {columns.map((column: any, index: number) => {
                  if (column.sortBy) {
                    return (
                      <Th
                        key={index}
                        sorted={sortBy === column.sortBy}
                        reversed={reverseSortDirection}
                        onSort={() => setSorting(column.sortBy as any)}
                        index={index}
                        id={`col-${column.id}`}
                        style={{ width: column?.width }}
                      >
                        {column.name}
                      </Th>
                    );
                  }
                  return (
                    <Table.Th
                      key={index}
                      className="text-center th-action"
                      fw={500}
                      fz={"sm"}
                    >
                      <UnstyledButton className="control">
                        <Group justify="center">{column.name}</Group>
                      </UnstyledButton>
                    </Table.Th>
                  );
                })}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        </div>
      </ScrollArea>
      <EditPatientModal
        modalOpened={modalOpened}
        setModalOpened={setModalOpened}
        patient={patient}
        fetchPatients={fetchPatients}
        setPatient={setPatient}
        setModalConfirmDeleteOpened={setModalConfirmDeleteOpened}
      />
      <ConfirmDeletePatientModal
        modalOpened={modalConfirmDeleteOpened}
        setModalOpened={setModalConfirmDeleteOpened}
        patient={patient}
        fetchPatients={fetchPatients}
      />
    </>
  );
};
export default PatientTable;
